'use client';

import { Link } from '@tanstack/react-router';
import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { theme } from '@/lib/ui/theme';

interface PricingTeaserSectionProps {
  isLoading?: boolean;
}

export function PricingTeaserSection({
  isLoading = false,
}: PricingTeaserSectionProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === 'dark';

  if (isLoading || !mounted) {
    return (
      <section className="px-6 py-12 md:px-16 md:py-16">
        <div className="mx-auto max-w-[800px]">
          <Skeleton className="mx-auto h-40 w-full rounded-2xl" />
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-12 md:px-16 md:py-16">
      <div
        className="mx-auto max-w-[800px] rounded-2xl p-8 text-center md:p-12"
        style={{
          background: theme.box.gradient.bg,
          border: `1px solid ${theme.box.purple.border[isDark ? 'dark' : 'light']}`,
        }}
      >
        <h2
          className="text-gradient-hero mb-3 text-3xl font-bold tracking-tight md:text-4xl"
          style={{ fontFamily: theme.fonts.display }}
        >
          $30 per seat. That's the whole price.
        </h2>
        <p
          className="text-foreground/70 mb-3 text-base md:text-lg"
          style={{ fontFamily: theme.fonts.body }}
        >
          7-day free trial. No usage fees, no surprises.
        </p>
        <p
          className="text-foreground/60 mb-8 text-sm md:text-base"
          style={{ fontFamily: theme.fonts.body }}
        >
          Early adopters get direct founder access and a say in what gets built.
        </p>
        <Link
          to="/pricing"
          className="text-foreground/80 inline-flex items-center gap-2 font-semibold underline transition-opacity hover:opacity-70"
          style={{
            fontFamily: theme.fonts.body,
            textDecorationColor: theme.colors.semantic.info,
          }}
        >
          See full pricing
          <span aria-hidden="true">→</span>
        </Link>
      </div>
    </section>
  );
}
